/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import { type FC } from 'react'
import ReactQuill from 'react-quill'

const QUIL_MODULES = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [
      { list: 'ordered' },
      { list: 'bullet' },
      { indent: '-1' },
      { indent: '+1' },
    ],
    [{ size: ['small', false, 'large', 'huge'] }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ color: [] }, { background: [] }],
    [{ font: [] }],
    [{ align: [] }],
    ['link', 'image'],
    ['clean'],
  ],
}

const QUIL_FORMATS = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'size',
  'heading',
  'color',
  'background',
  'font',
  'align',
  'link',
  'image',
]

export type Props = {
  value?: ReactQuill.Value,
  readOnly?: boolean,
  onChange?: (value: ReactQuill.Value) => void,
  placeholder?: string,
}

/**
 * TextEditor is described here.
 *
 * @example
 * ```jsx
 * <TextEditor />
 * ```
 *
 * @since 06/02/2024
 * @link https://react-styleguidist.js.org/docs/documenting.html
 */
const TextEditor: FC<Props> = ({
  value = '',
  readOnly = false,
  onChange = () => {},
  placeholder = ''
}) => {
  return (
    <ReactQuill
      theme='snow'
      value={value}
      preserveWhitespace
      placeholder={placeholder}
      modules={QUIL_MODULES}
      formats={QUIL_FORMATS}
      readOnly={readOnly}
      onChange={onChange}
    />
  )
}

export default TextEditor
